import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Logo } from '../components/ui/Logo';
import { Dropdown } from '../components/ui/Dropdown';
import { User, Menu, Home, Settings, HelpCircle, ExternalLink, Globe, CircleDollarSign, Pin, AlertTriangle, ShoppingCart, Check, ArrowRight, Link } from 'lucide-react';
import { ReactComponent as HandshakeIcon } from '../components/ui/icons/handshake.svg';
import { getAccountDropdownItems } from '../utils/getAccountDropdownItems';
import { useUser } from '../hooks/useUser';
import { useLanguage } from '../hooks/useLanguage';
import { ReactComponent as BotIcon } from '../components/ui/icons/bot.svg';

// SVG-иконка Telegram
const TelegramIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="12" fill="#229ED9"/>
    <path d="M17.5 7.5L15.5 17.5C15.5 17.5 15.25 18 14.75 18C14.5 18 14.25 17.75 14 17.5L11.5 15.5L10.5 16.5C10.5 16.5 10.5 16.5 10.25 16.5C10 16.5 10 16.25 10 16.25L9.5 13.5L15.25 8.75C15.5 8.5 15.25 8.5 15 8.75L8.5 13L6.5 12.5C6.5 12.5 6 12.25 6 12C6 11.75 6.5 11.5 6.5 11.5L17 7.25C17.25 7.25 17.5 7.25 17.5 7.5Z" fill="#fff"/>
  </svg>
);

export const PricingPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { currentLanguage, switchLanguage } = useLanguage();

  // Проверка PWA режима
  const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                (window.navigator as any).standalone === true;

  // Проверка Safe Area (только для iPhone)
  const hasSafeArea = window.matchMedia('(display-mode: standalone)').matches && 
                      getComputedStyle(document.documentElement).getPropertyValue('--safe-area-top') !== '0px';
  const safeAreaTop = getComputedStyle(document.documentElement).getPropertyValue('--safe-area-top');


  // Убираем фокус-подсветку у всех ссылок @ForsBotSupport, которые рендерятся через dangerouslySetInnerHTML
  React.useEffect(() => {
    const selector = `a[href="${process.env.REACT_APP_SUPPORT_TELEGRAM_URL}"]`;
    document.querySelectorAll(selector).forEach((el) => {
      el.classList.add('focus:outline-none', 'focus:ring-0', 'active:outline-none', 'active:ring-0');
    });
  }, [i18n.language]);

  const { user } = useUser();
  const accountItems = getAccountDropdownItems(t, navigate, user?.is_admin || false);

  const languageItems = [
    { id: 'eng', label: 'English', onClick: () => switchLanguage('ENG') },
    { id: 'rus', label: 'Русский', onClick: () => switchLanguage('RUS') }
  ];

  const pagesItems = [
    { id: 'home', label: t('home'), icon: Home, onClick: () => navigate('/') },
    { id: 'instructions', label: t('instructions'), icon: Settings, onClick: () => navigate('/instructions') },
    { id: 'faq', label: t('faq'), icon: HelpCircle, onClick: () => navigate('/faq') },
    { id: 'pricing', label: t('pricing'), icon: CircleDollarSign, onClick: () => navigate('/pricing') },
    { id: 'links', label: t('links'), icon: Link, onClick: () => navigate('/links') }
  ];

  return (
    <div className="min-h-screen bg-background" 
         style={{ paddingTop: (isPWA && hasSafeArea) ? `calc(5rem + ${safeAreaTop})` : '5rem' }}>
      <header className="fixed top-0 left-0 w-full z-50 bg-header border-b border-border">
        <div className="flex items-center justify-between w-full h-20 px-4">
          {/* Левая колонка */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-4">
              <Logo color="primary" className="" style={{ color: '#525252', marginTop: '1mm', width: 'clamp(48px, 5.3vw, 64px)', height: 'clamp(48px, 5.3vw, 64px)' }} />
              <div className="flex flex-col justify-center min-w-0">
                <h1 className="text-3xl font-extra-bold neon-glow whitespace-nowrap" style={{ color: '#525252' }}>
                  ForsBot
                </h1>
                <p
                  className="text-textSubtle truncate"
                  style={{ fontSize: 'clamp(8px, 2vw, 16px)' }}
                >
                  {t('main_slogan')}
                </p>
              </div>
            </div>
            </div>
          {/* Правая колонка */}
          <div className="flex items-center space-x-1 flex-shrink-0">
            <Dropdown
              id="account"
              trigger={<User className="w-5 h-5 text-iconPrimary" />}
              items={accountItems}
              align="right"
            />
            <Dropdown
              id="language"
              trigger={<span className="text-sm font-medium text-iconPrimary">{currentLanguage}</span>}
              items={languageItems}
              align="right"
            />
            <Dropdown
              id="pages"
              trigger={<Menu className="w-5 h-5 text-iconPrimary" />}
              items={pagesItems}
              align="right"
            />
          </div>
        </div>
      </header>
      <main className="w-full px-4 sm:px-6 lg:px-8 py-8 max-w-6xl mx-auto">
        <h2 className="flex items-center justify-center gap-2 text-2xl md:text-3xl font-bold text-text mb-4 md:mb-8 text-center font-sans">
          <CircleDollarSign className="w-7 h-7 text-white" />
          <span>{t('pricing')}</span>
        </h2>
        <div className="text-lg md:text-2xl text-textSubtle text-center mb-8 font-sans">
          {t('pricing_intro').split('@ForsBotSupport').map((part: string, idx: number, arr: string[]) =>
            idx < arr.length - 1 ? (
              <React.Fragment key={idx}>
                {part}
                <TelegramIcon className="w-5 h-5 inline-block align-middle -mt-2 mx-1" />{' '}
                <a
                  href={process.env.REACT_APP_SUPPORT_TELEGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline font-medium hover:underline focus:outline-none focus:ring-0 active:outline-none active:ring-0"
                >
                  {process.env.REACT_APP_SUPPORT_TELEGRAM_HANDLE}
                </a>
              </React.Fragment>
            ) : (
              part
            )
          )}
        </div>
        {/* Тарифы */}
        <section className="mb-8 md:mb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Один бот */}
            <div className="bg-header border border-border rounded-lg p-6 flex flex-col items-center text-center">
              <BotIcon className="w-7 h-7 text-white mb-2" />
              <h3 className="text-xl font-bold mb-2">{t('pricing_one_bot_title')}</h3>
              <div className="text-2xl font-extrabold text-text mb-2">
                {t('pricing_one_bot_price_amount')}
                <span className="text-lg font-bold text-textSubtle" dangerouslySetInnerHTML={{ __html: t('pricing_one_bot_price_unit') }} />
              </div>
              <p className="text-textSubtle text-base mb-2" dangerouslySetInnerHTML={{ __html: t('pricing_one_bot_desc')
                .split('https://t.me/ForsBotSupport').join(process.env.REACT_APP_SUPPORT_TELEGRAM_URL || 'https://t.me/ForsBotSupport')
                .split('@ForsBotSupport').join(process.env.REACT_APP_SUPPORT_TELEGRAM_HANDLE || '@ForsBotSupport') }} />
            </div>
            {/* Два бота */}
            <div className="bg-header border border-border rounded-lg p-6 flex flex-col items-center text-center">
              <div className="flex items-center mb-2">
                <BotIcon className="w-7 h-7 text-white mr-1" />
                <BotIcon className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">{t('pricing_two_bots_title')}</h3>
              <div className="text-2xl font-extrabold text-text mb-2">
                {t('pricing_two_bots_price_amount')}
                <span className="text-lg font-bold text-textSubtle" dangerouslySetInnerHTML={{ __html: t('pricing_two_bots_price_unit') }} />
              </div>
              <div className="text-lg font-bold underline text-orange-500 mb-1">{t('pricing_two_bots_discount')}</div>
              <p className="text-textSubtle text-base mb-2" dangerouslySetInnerHTML={{ __html: t('pricing_two_bots_desc')
                .split('https://t.me/ForsBotSupport').join(process.env.REACT_APP_SUPPORT_TELEGRAM_URL || 'https://t.me/ForsBotSupport')
                .split('@ForsBotSupport').join(process.env.REACT_APP_SUPPORT_TELEGRAM_HANDLE || '@ForsBotSupport') }} />
            </div>
          </div>
          <div className="text-center text-text mt-6 text-lg font-bold underline">
            {t('pricing_payment_terms')}
          </div>
          <div className="flex items-center justify-center mt-2 text-orange-500 text-lg font-bold underline">
            <AlertTriangle className="w-5 h-5 mr-2" /> {t('pricing_no_refund')}
          </div>
        </section>

        {/* Что включено в подписку */}
        <section className="mb-8 md:mb-16">
          <div className="bg-header border border-border rounded-lg p-6">
            <div className="flex items-center mb-4">
              <ShoppingCart className="w-6 h-6 text-primary mr-2" />
              <h3 className="text-lg md:text-xl font-bold text-text">{t('pricing_includes_title')}</h3>
            </div>
            <ul className="space-y-2 ml-2 md:ml-6">
              {(t('pricing_includes_list', { returnObjects: true }) as string[]).map((item, idx) => (
                <li key={idx} className="flex items-start text-textSubtle text-base md:text-lg"><Check className="w-5 h-5 min-w-[20px] min-h-[20px] flex-shrink-0 text-textSubtle mr-2" /> {item}</li>
              ))}
            </ul>
          </div>
        </section>

        {/* Важно */}
        <section className="mb-8 md:mb-16">
          <div className="bg-header border border-border rounded-lg p-6">
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-orange-500 mr-2" />
              <h3 className="text-lg md:text-xl font-bold">{t('pricing_important_title')}</h3>
            </div>
            <ul className="space-y-3 ml-2 md:ml-6 text-textSubtle">
              {(t('pricing_important_list', { returnObjects: true }) as string[]).map((rawItem, idx) => {
                const item = rawItem
                  .split('https://t.me/ForsBotSupport').join(process.env.REACT_APP_SUPPORT_TELEGRAM_URL || 'https://t.me/ForsBotSupport')
                  .split('@ForsBotSupport').join(process.env.REACT_APP_SUPPORT_TELEGRAM_HANDLE || '@ForsBotSupport');
                return (
                  <li key={idx} className="flex items-start text-textSubtle text-base md:text-lg"><Check className="w-5 h-5 min-w-[20px] min-h-[20px] flex-shrink-0 text-textSubtle mr-2" /> <span dangerouslySetInnerHTML={{ __html: item }} /></li>
                );
              })}
            </ul>
          </div>
        </section>

        {/* Призыв к действию */}
        <section className="mb-8 md:mb-16">
          <div className="bg-header border border-border rounded-lg p-6 flex flex-col items-center text-center">
            <div className="text-base md:text-lg font-bold text-textSubtle mb-2" 
              dangerouslySetInnerHTML={{ __html: t('pricing_cta').replace(
                /(<a [^>]*>@ForsBotSupport<\/a>)/,
                `<svg viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg' class='inline w-4 h-4 align-text-bottom mr-1' style='position:relative;top:-3px;'><circle cx='12' cy='12' r='12' fill='#229ED9'/><path d='M17.5 7.5L15.5 17.5C15.5 17.5 15.25 18 14.75 18C14.5 18 14.25 17.75 14 17.5L11.5 15.5L10.5 16.5C10.5 16.5 10.5 16.5 10.25 16.5C10 16.5 10 16.25 10 16.25L9.5 13.5L15.25 8.75C15.5 8.5 15.25 8.5 15 8.75L8.5 13L6.5 12.5C6.5 12.5 6 12.25 6 12C6 11.75 6.5 11.5 6.5 11.5L17 7.25C17.25 7.25 17.5 7.25 17.5 7.5Z' fill='#fff'/></svg>$1`
              )
                .split('https://t.me/ForsBotSupport').join(process.env.REACT_APP_SUPPORT_TELEGRAM_URL || 'https://t.me/ForsBotSupport')
                .split('@ForsBotSupport').join(process.env.REACT_APP_SUPPORT_TELEGRAM_HANDLE || '@ForsBotSupport') }}
            />
            <div className="text-lg md:text-xl font-bold" >{t('pricing_team_wishes')}</div>
          </div>
        </section>
      </main>
    </div>
  );
}; 